import {Button, Table as TableAnt} from 'antd';
import {useWatch} from 'antd/es/form/Form';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import {NamePath, StoreValue} from 'antd/es/form/interface';
import moment from 'moment';
import React, {useEffect, useState} from 'react';
import ReactQuill from 'react-quill';
import {useSearchParams} from 'react-router-dom';

import {normalizeTableValue} from '../../pages/esgManager/constants/commonInputFormFunction';
import {inputType} from '../../pages/esgManager/interface/inputType';
import QuestionnaireSwitch from '../../pages/esgManager/utils/QuestionnaireSwitch';
import {replaceCurrentAndPrevious} from '../../shared/checkSelectedYear';
import {formatIndian} from '../../utils/formatIndian';
import {Units} from '../Forms/Constants/CommonESGConstants/constants';

import {Title} from './Constants/Common';
import ModalAddNewToTable from './Utils/ModalAddNewToTable';

const QuestionnaireTableWithYearFixedRow: React.FC<{
  field: any;
  getFieldValue: (name: NamePath) => StoreValue;
}> = ({field, getFieldValue}) => {
  const {
    yearFieldName,
    pastYearsFieldName,
    pastYearsCountFieldName,
    excludeReportingYear
  } = field?.yearFixedRowsBasedOnDependency ?? {};
  const dependentTableKey = field?.relatedTableKey;

  const [showModel, setShowModel] = useState(false);
  const [editData, setEditData] = useState<any>();
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState<any[]>();
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [searchParams] = useSearchParams();
  const selectedYear = searchParams.get('selectedYear');

  const FI = useFormInstance();
  const {setFieldsValue} = FI;

  const dependentValue = useWatch(
    dependentTableKey ?? pastYearsCountFieldName,
    FI
  );

  const getDependentFieldValue = (key: string): any => {
    if (!dependentTableKey) return getFieldValue(key);

    const dependentTableValue =
      normalizeTableValue(getFieldValue(dependentTableKey)) ?? [];
    const tableData = Array.isArray(dependentTableValue)
      ? dependentTableValue
      : [dependentTableValue];

    return tableData.find(
      (row: any) =>
        row !== null && typeof row === 'object' && row[key] !== undefined
    )?.[key];
  };

  // Resolved during render so the reconciliation below re-runs whenever either
  // answer changes. Watching the whole related table is too coarse to rely on:
  // a row edit that does not surface as a new watched value leaves rows stale.
  const pastYearsAnswer = getDependentFieldValue(pastYearsFieldName);
  const pastYearsCountAnswer = getDependentFieldValue(pastYearsCountFieldName);

  // Reporting year + selected number of past years, e.g. '3 years' => 4 rows
  // (or only the past years when excludeReportingYear is set => 3 rows)
  const getYearFixedRows = (): any[] => {
    const pastYearsCount =
      pastYearsAnswer === 'Yes' ? parseInt(pastYearsCountAnswer, 10) || 0 : 0;

    // First option is the reporting year, followed by past years
    const yearOptions =
      field?.formList?.formFields
        ?.find((formField: any) => formField.fieldName === yearFieldName)
        ?.options?.slice(excludeReportingYear ? 1 : 0, pastYearsCount + 1) ??
      [];

    const existing =
      normalizeTableValue(getFieldValue([field.fieldName])) ?? [];
    const existingRows = Array.isArray(existing) ? existing : [];

    return yearOptions.map((option: any, index: number) => {
      // Rows saved without a year (e.g. auto-populated) belong to the reporting year
      const hadRecord =
        existingRows.find(
          (row: any) => row?.[yearFieldName] === option.value
        ) ??
        (index === 0 && !excludeReportingYear
          ? existingRows.find((row: any) => !row?.[yearFieldName])
          : undefined);

      return {
        ...hadRecord,
        _id: hadRecord?._id ?? `year_${index}`,
        [yearFieldName]: option.value
      };
    });
  };

  useEffect(() => {
    const existing =
      normalizeTableValue(getFieldValue([field.fieldName])) ?? [];
    const yearFixedRows = getYearFixedRows();

    const shouldUpdate =
      !Array.isArray(existing) ||
      existing.length !== yearFixedRows.length ||
      yearFixedRows.some(
        (row: any, index: number) =>
          existing[index]?.[yearFieldName] !== row[yearFieldName]
      );

    if (shouldUpdate) {
      setFieldsValue({[field.fieldName]: yearFixedRows});
    }
    setDataSource(yearFixedRows);
  }, [dependentValue, pastYearsAnswer, pastYearsCountAnswer]);

  const srNoColumn = [
    {
      title: 'S. No.',
      dataIndex: 'srNo',
      render: (id: any, record: any, index: any) => {
        return (page - 1) * pageSize + ++index;
      },
      width: 'fit-content'
    }
  ];

  const column = [
    {
      title: Title.actionTitle,
      dataIndex: 'operation',
      fixed: 'right',
      width: '100px',
      render: (value: any, record: any) => {
        return dataSource.length >= 1 ? (
          <Button
            onClick={() => {
              setShowModel(true);
              setEditData(record);
              setIsEdit(true);
              setEditId(record._id);
            }}
            type="link"
            style={{marginRight: '20px', padding: '4px 0px'}}
          >
            Edit
          </Button>
        ) : null;
      }
    }
  ];

  const addedColumns = field?.formList?.formFields.map((field: any) => ({
    title: replaceCurrentAndPrevious(field.fieldLabel, selectedYear ?? ''),
    dataIndex: field.fieldName,
    render: (value: any) => {
      if (field.type === inputType.DATEPICKER) {
        return value
          ? moment(value).format(field?.dateFormat || 'DD-MM-YYYY')
          : '-';
      }
      if (field.type === inputType.PERCENTAGE) {
        return value ? `${value}${Units.percentage}` : '-';
      }
      if (field.type === inputType.NUMBER) {
        return value ? formatIndian(value) : '-';
      }
      if (field.type === inputType.WYSIWYG) {
        return value ? (
          <ReactQuill
            className="quill-table-cell"
            value={value}
            readOnly={true}
            theme={'bubble'}
          />
        ) : (
          '-'
        );
      }
      if (Array.isArray(value)) {
        return value.length ? value.join(', ') : '-';
      }

      return value || '-';
    }
  }));

  const columns = [...srNoColumn, ...addedColumns, ...column];

  return (
    <>
      <TableAnt
        className="questionnaire-table"
        rowKey="_id"
        dataSource={dataSource}
        columns={columns as any}
        scroll={{x: 'max-content'}}
        pagination={{
          pageSize,
          onChange(current) {
            setPage(current);
          }
        }}
      />

      <ModalAddNewToTable
        open={showModel}
        onCancel={() => {
          setShowModel(false);
        }}
        field={field}
        data={isEdit ? editData : undefined}
        title={
          isEdit && editData?.[yearFieldName]
            ? `Edit ${editData[yearFieldName]}`
            : undefined
        }
        FormComponent={field?.formList?.formFields.map(
          (formField: any, index: any) => (
            <QuestionnaireSwitch
              field={{
                fieldType: formField.type,
                fieldLabel: replaceCurrentAndPrevious(
                  formField.fieldLabel,
                  selectedYear ?? ''
                ),
                textLimit: formField.textLimit,
                options: formField.options,
                heading: replaceCurrentAndPrevious(
                  formField.heading,
                  selectedYear ?? ''
                ),
                colSpan: formField.colSpan,
                restrictFutureDateSelection:
                  formField.restrictFutureDateSelection,
                fieldName: formField.fieldName,
                units: formField.units,
                dateFormat: formField.dateFormat,
                relatedKey: formField.relatedKey,
                formula: formField.formula,
                roundTo: formField.roundTo,
                // Year is fixed per row, so it is shown but cannot be changed
                disabled:
                  formField.fieldName === yearFieldName || formField.disabled,
                fileUploadAction: formField.fileUploadAction,
                fileUploadValidExtensions: formField.fileUploadValidExtensions,
                fileUploadMaxSize: formField.fileUploadMaxSize,
                fileUploadAllowMultipleFiles:
                  formField.fileUploadAllowMultipleFiles,
                disabledOn: formField.disabledOn,
                requiredOn: formField.requiredOn,
                conditionalVisibility: formField.conditionalVisibility
              }}
              key={index}
            />
          )
        )}
        table={true}
        buttonText={'Save'}
        setDataSource={setDataSource}
        dataSource={dataSource}
        editId={editId}
        isEdit={isEdit}
      />
    </>
  );
};

export default QuestionnaireTableWithYearFixedRow;
